import styles from './styles.module.scss';
import { Tooltip } from 'radix-ui';
import { type UpgradeConfigKey } from '../../types/game-types';
import { type RefObject, type RefCallback } from 'react';
import { upgradesConfig } from '../../upgrades-config';
import { SkillTree } from 'skill-tree/src/components/SkillTree';
import Draggable from 'react-draggable';
import { PurchaseUpgrade } from '../PurchaseUpgrade';
import { UpgradeNode } from '../UpgradeNode';
import { NodeLink } from '../NodeLink';
import { ComposableIconsProvider } from '@game-libs/exc-libs/src/composable-icons';
import { useScrollZoom } from '@game-libs/exc-libs';
import { spritesheetConfig } from '../../config';
import { GeneratorContextProvider } from '../../store/generator-context';

interface TreeViewProps {
  sizeRef: RefCallback<HTMLDivElement>;
  size: { width: number; height: number };
  nodeRef: RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  onDragChange: (isDragging: boolean) => void;
  selectedElement: UpgradeConfigKey;
}

export const TreeView = ({ sizeRef, size, nodeRef, isDragging, onDragChange, selectedElement }: TreeViewProps) => {
  const [zoom] = useScrollZoom(1, 0.001, 0.7, 2);
  const generatorsUpgradesConfig = upgradesConfig;

  return (
    <div className={styles.upgradesBoard} ref={sizeRef}>
      <Tooltip.Provider>
        <ComposableIconsProvider value={spritesheetConfig}>
          <Draggable
            nodeRef={nodeRef}
            bounds={{
              left: -size.width * 0.25, // Works for pannableArea width: 200%
              right: size.width * 0.25,
              top: -size.height * 0.5, // Works for pannableArea height: 400%
              bottom: size.height * 0.5,
            }}
            onStart={() => {
              onDragChange(true);
            }}
            onStop={() => {
              onDragChange(false);
            }}
          >
            <div
              style={{
                willChange: 'transform, scale',
                scale: `${zoom}`,
                transformOrigin: 'center',
              }}
              ref={nodeRef}
              className={`${styles.pannableArea} ${isDragging && styles.isDragging}`}
            >
              {selectedElement && generatorsUpgradesConfig[selectedElement] && (
                <GeneratorContextProvider value={selectedElement}>
                  <SkillTree
                    skillsConfig={generatorsUpgradesConfig[selectedElement]!}
                    linkComponent={({ skill }) => <NodeLink skill={skill} />}
                    zoom={zoom}
                  >
                    {({ skill }) => {
                      return (
                        <Tooltip.Root disableHoverableContent delayDuration={0}>
                          <UpgradeNode skill={skill} />
                          <Tooltip.Portal container={document.getElementById('upgrades-dialog')}>
                            <Tooltip.Content
                              sideOffset={16}
                              onPointerDownOutside={(event) => {
                                event.preventDefault();
                              }}
                            >
                              <PurchaseUpgrade id={skill.id}></PurchaseUpgrade>
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      );
                    }}
                  </SkillTree>
                </GeneratorContextProvider>
              )}
            </div>
          </Draggable>
        </ComposableIconsProvider>
      </Tooltip.Provider>
    </div>
  );
};
